import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAccountOverview, useCreateRecharge } from '@/hooks/useAccount'
import { 
  CreditCard, 
  DollarSign, 
  TrendingUp, 
  Wallet,
  ArrowRight,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

const rechargeSchema = z.object({
  amount: z.number().min(1, '充值金额必须大于0'),
  description: z.string().optional(),
  paymentMethod: z.string().min(1, '请选择支付方式'),
})

type RechargeForm = z.infer<typeof rechargeSchema>

export default function Recharge() {
  const [isProcessing, setIsProcessing] = useState(false)
  const { toast } = useToast()
  const { data: accountOverview, isLoading: overviewLoading, refetch: refetchOverview } = useAccountOverview()
  const createRecharge = useCreateRecharge()

  const form = useForm<RechargeForm>({
    resolver: zodResolver(rechargeSchema),
    defaultValues: {
      amount: 0,
      description: '',
      paymentMethod: '',
    },
  })

  const onSubmitRecharge = async (data: RechargeForm) => {
    try {
      setIsProcessing(true)
      
      // 显示MOCK支付流程
      toast({
        title: '正在跳转支付...',
        description: `正在跳转到${data.paymentMethod === 'ALIPAY' ? '支付宝' : data.paymentMethod === 'WECHAT' ? '微信支付' : '银行转账'}页面`,
      })
      
      // 模拟支付延迟
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      await createRecharge.mutateAsync({
        amount: data.amount,
        description: data.description,
        paymentMethod: data.paymentMethod,
      })
      
      toast({
        title: '充值成功',
        description: `已成功充值 ¥${data.amount}，支付方式：${data.paymentMethod === 'ALIPAY' ? '支付宝' : data.paymentMethod === 'WECHAT' ? '微信支付' : '银行转账'}`,
      })
      
      form.reset()
      refetchOverview()
    } catch (error) {
      toast({
        title: '充值失败',
        description: error instanceof Error ? error.message : '充值失败，请重试',
        variant: 'destructive',
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const quickAmounts = [50, 100, 200, 500, 1000, 2000]

  return (
    <div className="flex-1 space-y-6 p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">账户充值</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* 充值表单 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              充值信息
            </CardTitle>
            <CardDescription>
              选择充值金额和支付方式，完成充值操作
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmitRecharge)} className="space-y-6">
              {/* 快速金额选择 */}
              <div className="space-y-3">
                <Label>选择充值金额</Label>
                <div className="grid grid-cols-3 gap-3">
                  {quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={form.watch('amount') === amount ? 'default' : 'outline'}
                      className="h-12"
                      onClick={() => form.setValue('amount', amount)}
                    >
                      ¥{amount}
                    </Button>
                  ))}
                </div>
              </div>

              {/* 自定义金额 */}
              <div className="space-y-2">
                <Label htmlFor="amount">自定义金额</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="请输入充值金额"
                  {...form.register('amount', { valueAsNumber: true })}
                />
                {form.formState.errors.amount && (
                  <p className="text-sm text-red-500">{form.formState.errors.amount.message}</p>
                )}
              </div>
              
              {/* 支付方式选择 */}
              <div className="space-y-2">
                <Label htmlFor="paymentMethod">支付方式</Label>
                <Select value={form.watch('paymentMethod')} onValueChange={(value) => form.setValue('paymentMethod', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="请选择支付方式" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALIPAY">支付宝</SelectItem>
                    <SelectItem value="WECHAT">微信支付</SelectItem>
                    <SelectItem value="BANK">银行转账</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.paymentMethod && (
                  <p className="text-sm text-red-500">{form.formState.errors.paymentMethod.message}</p>
                )}
              </div>
              
              {/* 备注信息 */}
              <div className="space-y-2">
                <Label htmlFor="description">备注信息</Label>
                <Textarea
                  id="description"
                  placeholder="可选：填写充值备注"
                  {...form.register('description')}
                />
              </div>
              
              {/* 提交按钮 */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isProcessing || createRecharge.isPending}
                size="lg"
              >
                {isProcessing || createRecharge.isPending ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    充值中...
                  </>
                ) : (
                  <>
                    确认充值
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* 账户信息和余额预览 */}
        <div className="space-y-6">
          {/* 当前账户信息 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                当前账户
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {overviewLoading ? (
                <div className="space-y-3">
                  <div className="h-4 bg-muted rounded animate-pulse" />
                  <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">账户余额</span>
                    <span className="text-2xl font-bold text-green-600">
                      ¥{(accountOverview?.balance || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">冻结余额</span>
                    <span className="text-lg font-medium">
                      ¥{(accountOverview?.frozenBalance || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">累计充值</span>
                    <span className="text-lg font-medium text-blue-600">
                      ¥{(accountOverview?.totalRecharge || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">累计消费</span>
                    <span className="text-lg font-medium text-red-600">
                      ¥{(accountOverview?.totalConsumption || 0).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* 充值后余额预览 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                充值预览
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">当前余额</span>
                <span className="font-medium">¥{(accountOverview?.balance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">充值金额</span>
                <span className="font-medium text-green-600">+¥{(form.watch('amount') || 0).toFixed(2)}</span>
              </div>
              <div className="border-t pt-4 flex justify-between items-center">
                <span className="text-sm font-medium">充值后余额</span>
                <span className="text-xl font-bold text-green-600">
                  ¥{((accountOverview?.balance || 0) + (form.watch('amount') || 0)).toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* 支付说明 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                支付说明
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <span className="text-sm">支持支付宝、微信支付、银行转账等多种支付方式</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <span className="text-sm">充值金额实时到账，可用于短信发送等业务</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5" />
                <span className="text-sm">当前为测试环境，支付流程已模拟，实际充值不会扣费</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
